"use client";

import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import ErrorMessage from "@/components/ui/ErrorMessage";
import Input from "@/components/ui/Input";
import SelectInput from "@/components/ui/SelectInput";
import { Separator } from "@/components/ui/Separator";
import usePost from "@/hooks/usePost";
import { registerSchema } from "@/schemas/auth/register";
import {
  checkIfEmailOrUsernameExists,
  fetchUsernameSuggestions,
  signUpByLocation,
} from "@/services/register";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useStore from "store";
import ActivateAccountAlert from "./ActivateAccountAlert";
import RegisterByLocationDialog from "./RegisterByLocationDialog";

const RegisterForm = () => {
  const router = useRouter();
  const { referralLink, registerId, byGoogle } = router.query;
  const isRouterReady = router.isReady;
  const isByGoogle = byGoogle == "true";
  const { t } = useTranslation("register");

  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showActivateAccountAlert, setShowActivateAccountAlert] =
    useState(false);
  const signupBySuper = useStore(state => state.signupBySuper);
  const isLoggedIn = useStore(state => state.isLoggedIn);
  const { handlePost, error, isLoading } = usePost(handleRegister);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      gender: "1",
      acceptedPrivacy: false,
    },
  });

  const {
    handlePost: mutateFetchUsernameSuggestions,
    value: usernameSuggestions,
  } = usePost(fetchUsernameSuggestions);

  useEffect(() => {
    if (isLoggedIn) {
      router.push("/dashboard");
    }
  }, [isLoggedIn, router]);

  useEffect(() => {
    if (isRouterReady && isByGoogle && isMobile) {
      setShowLocationDialog(true);
    }
  }, [isRouterReady, isByGoogle]);

  async function handleRegister(values) {
    const { passwordConfirmation, ...others } = values;
    try {
      await checkIfEmailOrUsernameExists(others);
    } catch (e) {
      mutateFetchUsernameSuggestions(others.username);
      throw e;
    }

    if (referralLink) {
      await signupBySuper(others, referralLink);
      setShowActivateAccountAlert(true);
    } else {
      const res = await signUpByLocation(others);
      router.push("?registerId=" + res?.id);
      setShowLocationDialog(true);
    }

    reset();
  }

  return (
    <form onSubmit={handleSubmit(handlePost)} className="w-full grid">
      <div className="flex items-center gap-2 mb-4">
        <Separator />
        <span className="text-muted-foreground">{t("or")}</span>
        <Separator />
      </div>

      <ErrorMessage error={error} />

      <label className="block">
        <span className="block font-medium"> {t("username")}</span>
        <Input
          {...register("username")}
          type="text"
          errorMessage={t(errors.username?.message)}
          placeholder={t("username")}
          autoFocus
        />
      </label>

      {usernameSuggestions && (
        <div className="bg-success/10 p-2 rounded-xl mt-3 flex-wrap items-center">
          <span className="me-2 text-success">{t("you_can_use")}:</span>
          {usernameSuggestions.map(username => (
            <Button
              key={username}
              onClick={() => setValue("username", username)}
              variant="link"
              className="me-2 px-0 h-fit underline text-success text-nowrap"
              size="sm"
              type="button"
            >
              {username}
            </Button>
          ))}
        </div>
      )}

      <label className="block mt-3">
        <span className="block font-medium">{t("email")}</span>
        <Input
          {...register("email")}
          type="email"
          errorMessage={t(errors.email?.message)}
          placeholder={t("email")}
        />
      </label>

      <label className="block mt-3">
        <span className="block font-medium">{t("phone_number")}</span>
        <Input
          {...register("phone")}
          dir="ltr"
          type="text"
          errorMessage={t(errors.phone?.message)}
          className="rtl:text-end"
          placeholder={t("phone_number")}
          maxLength={10}
        />
      </label>

      <label className="block mt-3">
        <span className="block font-medium"> {t("gender")}</span>
        <SelectInput
          control={control}
          name="gender"
          items={[
            { name: t("male"), value: "1" },
            { name: t("female"), value: "0" },
          ]}
          placeholder={t("gender")}
          errorMessage={t(errors.gender?.message)}
        />
      </label>

      <label className="block mt-3">
        <span className="block font-medium">{t("password")}</span>
        <Input
          {...register("password")}
          errorMessage={t(errors.password?.message)}
          type="password"
          placeholder={t("password")}
        />
      </label>

      <label className="block mt-3">
        <span className="block font-medium">{t("confirm_password")}</span>
        <Input
          {...register("passwordConfirmation")}
          errorMessage={t(errors.passwordConfirmation?.message)}
          type="password"
          placeholder={t("confirm_password")}
        />
      </label>

      <div className="flex flex-col mt-3">
        <label className="flex items-center">
          <Checkbox
            onCheckedChange={event => setValue("acceptedPrivacy", event)}
            defaultChecked={false}
          />
          <span className="text-muted-foreground ms-2">
            {t("i_accept")}{" "}
            <Link href="/privacy-policy" target="_blank" passHref>
              <Button
                type="button"
                variant="link"
                className="p-0 h-fit underline"
              >
                {t("privacy_policy")}
              </Button>
            </Link>
          </span>
        </label>

        {errors.acceptedPrivacy && (
          <span className="text-destructive text-sm block mt-[6px]">
            {t(errors.acceptedPrivacy.message)}
          </span>
        )}
      </div>

      <Button isLoading={isLoading} type="submit" className="w-full mt-4">
        {t("register_button")}
      </Button>

      {showActivateAccountAlert && <ActivateAccountAlert />}

      <RegisterByLocationDialog
        registerId={registerId}
        isByGoogle={isByGoogle}
        showLocationDialog={showLocationDialog}
        setShowLocationDialog={setShowLocationDialog}
      />
    </form>
  );
};

export default RegisterForm;
